import { getAllMetrics, getBracketRanking, getBracketTournamentLastRound, getRanking, getResultsValues, getSingleMetric, isBracket, isConnect3, isConnectAll, isMilestone, isRaffle, isTopTeam, setActionItem, setConnect3Positions, setParticipantDefaultValues, setParticipantNoActionsHistory, setPaymentDate, setQualifier } from "./games-player-breakdown";
import { GameActionType, HistoricalTeamObj, HTournamentInfo, IHGameParams, IHGameStandardize, IHMetricDetail, IHRewards, IHSelectedMetric, MetricValues, StandardizeRewards } from "./games-parameter.model";
import { GameType } from "./games.model";

export const TIMEFORMAT = {
    MINUTES: 60,
    HOURS: 60 * 60,
    DAYS: 24 * 60 * 60,
} as const;

const MMReawardsAsPositions = {
    Reward: 0,
    MaxReward: 1
} as const;

type HistoryRewardsAsPosition = {
    [position: number]: {
        [ActionType: number]: IHRewards;
    };
};

type MetricAsPosition = {
    [position: number]: IHSelectedMetric;
}
/**
 * 
 * @returns a object, the position as a key and all the reward for that position example:
 * {
 *    0: {
 *        0: IHRewards,
 *        1: IHRewards,
 *    }
 * }
 * where the first 0 is the position 0, inside the objetc, we have 0 (coins), 1 (xp), and so on
 * so, if I want to know how many coins are in the position zero,
 * obj[0][0] // coins
 * obj[0][1] // xp
 */
const getRewardsByPositions = (HistoryRewards: Array<IHRewards>) => {
    const rewardsAsPositions = HistoryRewards.reduce((acc, element) => {
        const position = element.Position === null ? 0 : element.Position;

        if (!acc[position]) return {
            ...acc,
            [position]: {
                [element.ActionType]: element,
            },
        };

        return {
            ...acc,
            [position]: {
                ...acc[position],
                [element.ActionType]: element,
            }
        };

    }, {} as HistoryRewardsAsPosition);
    return rewardsAsPositions;
};

const getSelectedMetricByPosition = (SelectedMetrics: Array<IHSelectedMetric>) => {
    const metricAsPosition = SelectedMetrics.reduce((acc, element) => {
        if (element.Position !== null && !element.IsQualifier) {
            return {
                ...acc,
                [element.Position]: element,
            }
        }
        return acc;
    }, {} as { [position: number]: IHSelectedMetric });
    return metricAsPosition;
};

/**
 * HOF
 * @param rewardsAsPosition 
 * @returns a function(name: string, position: number)
 */
const getStandarize = (rewardsAsPosition: HistoryRewardsAsPosition, metricAsPosition: MetricAsPosition = {}, HMetricDetails: IHMetricDetail = {}) => {
    return (name: string, position: number) => {
        if (!rewardsAsPosition[position]) return null;
        const metricId = metricAsPosition[position]?.MetricID;
        const metricDetail = metricId && HMetricDetails[metricId] ? {
            MetricDetail: HMetricDetails[metricId]
        } : {};

        const positionMetricObj = metricAsPosition[position] ? {
            metricTarget: {
                ...metricAsPosition[position],
                ...metricDetail,
            },
        } : {};
        const reward: StandardizeRewards = {
            name,
            coins: rewardsAsPosition[position][GameActionType.Coin]?.RewardValue || rewardsAsPosition[position][GameActionType.CoinRatio]?.RewardValue || 0,
            xp: rewardsAsPosition[position][GameActionType.XP]?.RewardValue || rewardsAsPosition[position][GameActionType.XPRatio]?.RewardValue || 0,
            badges: rewardsAsPosition[position][GameActionType.Badge]?.RewardValue,
            customReward: rewardsAsPosition[position][GameActionType.Custom]?.RewardText,
            customRewardValue: rewardsAsPosition[position][GameActionType.Custom]?.RewardValue,
            gems: rewardsAsPosition[position][GameActionType.Gem]?.RewardValue,
            ...positionMetricObj,
            position,
        };
        return reward;
    };
}

const getMultiplierMadnessRewards = (game: IHGameParams) => {
    const rewardsAsPosition = getRewardsByPositions(game.HistoryRewards);
    if (!rewardsAsPosition[MMReawardsAsPositions.Reward]) return [];

    const getStandarizeReward = getStandarize(rewardsAsPosition);
    const allRewards: StandardizeRewards[] = [
        getStandarizeReward('REWARDS_PER_METRICS', MMReawardsAsPositions.Reward),
        getStandarizeReward('MAX_PAYOUT_POSSIBLE', MMReawardsAsPositions.MaxReward) ?? [],
    ].flat();
    return allRewards;
};

const getRewardsByPosition = (game: IHGameParams) => {
    const rewardsAsPosition = getRewardsByPositions(game.HistoryRewards);
    const metricAsPosition = getSelectedMetricByPosition(game.HistorySelectedMetrics);

    const positions = Object.keys(rewardsAsPosition);
    const getStandarizeReward = getStandarize(rewardsAsPosition, metricAsPosition, game.HMetricDetails);
    const allRewards: StandardizeRewards[] = positions.map((key, i) => getStandarizeReward(`${key}`, +key));
    if (isMilestone(game.GameType)) return allRewards.reverse();
    return allRewards;
};

const getRewardsByRank = (game: IHGameParams) => {
    const rewardsAsPosition = getRewardsByPositions(game.HistoryRewards);
    const getStandarizeReward = getStandarize(rewardsAsPosition);
    const positions = Object.keys(rewardsAsPosition);
    const allRewards: StandardizeRewards[] = positions.map((key) => getStandarizeReward(`${key}`, +key));
    return allRewards;
};

export const getStandardizeRewards = (game: IHGameParams): Array<StandardizeRewards> => {
    type RewardFn = {
        [gameType: number]: (game: IHGameParams) => StandardizeRewards[];
    }
    const rewardsFn: RewardFn = {
        [GameType.MultiplierMadness]: getMultiplierMadnessRewards,
        [GameType.TopDog]: getRewardsByRank,
        [GameType.Bullseye]: getRewardsByPosition,
        [GameType.Milestone]: getRewardsByPosition,
        [GameType.TopTeam]: getRewardsByRank,
        [GameType.Raffle]: getRewardsByRank,

        [GameType.Brackets]: getRewardsByRank,
        [GameType.Connect3]: getRewardsByRank,
        [GameType.Blackout]: getRewardsByRank,
    }
    return rewardsFn[game.GameType](game) || [];
};


export const getTimeSinceLastUpdateFormat = (time: number = 0, localizationFn = (key: string, defaultText?: string) => defaultText): string => {
    const date = {
        days: Math.floor(time / TIMEFORMAT.DAYS),
        hours: time / TIMEFORMAT.HOURS
    }
    if (date.days >= 1) {
        const key = date.days === 1 ? 'Day' : 'Days';
        return `${date.days} ${localizationFn(key, key)}`;
    };
    return localizationFn('Less_Than_Day');
};

type SParticipantsProps = {
    participants: any[];
    actionHistory: any[];
    game: IHGameStandardize;
    localizationFn: (key: string, defaultText?: string) => string;
    transformDateFn: (value: Date | string | number, format?: string, timezone?: string, locale?: string) => string;
    teamsInfo: { [key: number]: HistoricalTeamObj };
    tournamentInfo: HTournamentInfo;
};

export const standardizeParticipants = ({
    participants,
    actionHistory,
    game,
    localizationFn = (key: string, defaultText?: string) => defaultText,
    tournamentInfo,
    transformDateFn, 
    teamsInfo = {}
}: SParticipantsProps) => {
    const ranking = isBracket(game.GameType) ? getBracketRanking(game, actionHistory, tournamentInfo) : getRanking(participants, game, actionHistory, teamsInfo);
    angular.forEach(participants, (participantObject, key) => {
        //Verifying if user has joined the game
        if (!participantObject.participant?.AlreadyJoined) {
            participantObject = setParticipantDefaultValues(participantObject, game.GameType, localizationFn);
            return;
        }

        //To Get Action Coin, XP, Badge History
        if (actionHistory.length > 0) {
            participantObject = setParticipantNoActionsHistory(participantObject);

            angular.forEach(actionHistory, (actionItem) => {
                if (actionItem.LoginID == participantObject.participant?.LoginID) {
                    //ActionItem
                    participantObject = setActionItem(participantObject, actionItem);
                }
            });
            participantObject = setPaymentDate(participantObject, localizationFn, transformDateFn);

        } else {
            participantObject = setParticipantNoActionsHistory(participantObject);
        };

        //To Get Metric and Qualifier per participant.
        const allMetrics = getAllMetrics(game);
        // set qualifier
        const qualifierMetric = game.qualifierDefinition;
        participantObject = setQualifier(participantObject, qualifierMetric);

        if (allMetrics && allMetrics.length > 0) {
            const myMetric = game.metricDefinition;
            const metricValue: MetricValues = participantObject.metricsValues?.find(m => m.MetricID == myMetric?.MetricID);
            participantObject.metric = metricValue ? metricValue.MetricDisplayValue : '---';
            participantObject.metricValue = metricValue ? metricValue.MetricValue : undefined;

            if (isTopTeam(game.GameType)) {
                const teamValue = teamsInfo[participantObject?.participant?.TeamID];
                const teamMetricValue = teamValue?.metricsValues?.find(m => m.MetricID == myMetric?.MetricID);
                participantObject.metric = teamMetricValue?.MetricDisplayValue == null ? '---' : participantObject.metric;
                participantObject.metricValue = teamMetricValue?.MetricDisplayValue == null ? undefined : participantObject.metricValue;
                if (participantObject.participant && teamValue?.Team) {
                    participantObject.participant.Team = teamValue?.Team;
                }
            }

            if (isConnect3(game.GameType) || isConnectAll(game.GameType)) {
                const metrics = allMetrics.filter(m => !m.IsQualifier);
                participantObject = setConnect3Positions(participantObject, metrics);
            }
        } else {
            participantObject.metric = '---';
            participantObject.qualifier = '---';
            participantObject.passed = false;
            if (isConnect3(game.GameType) || isConnectAll(game.GameType)) {
                participantObject.connect3Metrics = [];
            }
            participantObject.results = localizationFn("No_Results", "No Results");
        }

        if (qualifierMetric && participantObject.passed === false && !isBracket(game.GameType) && !isRaffle(game.GameType)) {
            participantObject.results = localizationFn("Disqualified", "Disqualified");
        } else {
            participantObject.results = getResultsValues(participantObject, game, localizationFn, ranking);
            if (isBracket(game.GameType) && participantObject.ruleObject?.MyRankingObj?.MyRanking) {
                participantObject.resultsNumber = getBracketTournamentLastRound(participantObject.results, participantObject.ruleObject.MyRankingObj.MyRanking, tournamentInfo.NumberOfRounds, localizationFn);
            }
        }

    });
    return participants;
}
